import { CssBaseline, ThemeProvider } from '@mui/material';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import type { FC, PropsWithChildren, ReactNode } from 'react';
import { Suspense, lazy, useEffect, useState } from 'react';
import { useLocation, useNavigation } from 'react-router';

import NotificationComponent from '../components/molecules/notification';
import PageSpinner from '../components/molecules/pageSpinner';
import AlertDialog from '../components/organisms/alertDialog';
import SideMenu from '../components/organisms/sideMenu';
import { customGoTheme } from '../material-ui-theme/tsGoTheme';
import usePortal from '../utils/usePortal';
import { useZustand } from '../zustandStore';

import { excludedPaths, queryClient } from './queryClient';

const ReactQueryDevtoolsProduction = lazy(() =>
	import('@tanstack/react-query-devtools/build/modern/production.js').then(d => ({
		default: d.ReactQueryDevtools,
	})),
);

const Root: FC<PropsWithChildren<{ children: ReactNode }>> = ({ children }) => {
	const navigation = useNavigation();
	const location = useLocation();
	const [showDevtools, setShowDevtools] = useState(false);
	const isInExcludedList = excludedPaths.some(path => location.pathname.includes(path));
	const shouldRenderSideMenu = location.pathname !== '/' && !isInExcludedList;
	queryClient.setDefaultOptions({
		queries: {
			enabled: shouldRenderSideMenu,
		},
	});

	const { pageSpinner } = useZustand(state => ({
		...state,
	}));
	const { isLoading: isPageLoading } = pageSpinner;
	const NotificationPortal = usePortal('notificationWrapper', <NotificationComponent />);
	const DialogPortal = usePortal('dialogWrapper', <AlertDialog />);

	useEffect(() => {
		globalThis.toggleDevtools = () => setShowDevtools(old => !old);
	}, []);

	useEffect(() => {
		if (!shouldRenderSideMenu) {
			return;
		}
		const menuWrapper = document.querySelector('#main-sideMenu');
		const hasOpenChild = menuWrapper?.querySelector('.side-menu-open');
		const collapsedChildren = menuWrapper?.querySelectorAll('.side-menu-collapsed');
		const collapsedAndOpenChild = menuWrapper?.querySelector('.side-menu-collapsed .open');

		menuWrapper?.classList.toggle('hasSidemenuOpen', Boolean(hasOpenChild));

		menuWrapper?.classList.toggle(
			'hasDoubleSidemenuCollapsed',
			Boolean(collapsedChildren && collapsedChildren.length >= 2),
		);

		menuWrapper?.classList.toggle(
			'hasCollapsedAndOpen',
			Boolean(collapsedChildren && collapsedChildren.length >= 2 && collapsedAndOpenChild),
		);
	}, [shouldRenderSideMenu]);

	return (
		<QueryClientProvider client={queryClient}>
			<ThemeProvider theme={customGoTheme}>
				<CssBaseline />
				{shouldRenderSideMenu ? <SideMenu /> : null}
				<>{NotificationPortal}</>
				<>{DialogPortal}</>
				<PageSpinner isLoading={isPageLoading || navigation.state === 'loading'} />
				{children}
			</ThemeProvider>
			<ReactQueryDevtools
				initialIsOpen={false}
				buttonPosition={globalThis.IS_PLAYWRIGHT_RUNNING ? 'top-left' : 'bottom-right'}
			/>
			{/*  calling window.toggleDevtools() will download the devtools bundle and show them. */}
			{showDevtools && (
				<Suspense fallback={null}>
					<ReactQueryDevtoolsProduction />
				</Suspense>
			)}
		</QueryClientProvider>
	);
};

export default Root;
